
import React, { useState } from 'react';

interface LoginViewProps {
  onLoginSuccess: (token: string, remember: boolean) => void;
  onCancel?: () => void;
}

const LoginView: React.FC<LoginViewProps> = ({ onLoginSuccess, onCancel }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (res.ok) {
        const data = await res.json();
        onLoginSuccess(data.token, remember);
      } else {
        const data = await res.json();
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-10 sm:mt-20">
      <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 shadow-xl relative overflow-hidden">
        {/* Contextual Back Button */}
        {onCancel && (
          <button 
            onClick={onCancel}
            className="absolute top-6 left-6 text-gray-400 hover:text-indigo-600 transition-colors flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
          >
            <i className="fa-solid fa-arrow-left-long"></i>
            Back to Feed
          </button>
        )}

        <div className="text-center mb-8 pt-8 sm:pt-4">
          <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="fa-solid fa-lock text-2xl"></i>
          </div>
          <h2 className="text-2xl font-bold">Admin Login</h2>
          <p className="text-gray-500 text-sm mt-2">Enter your credentials to access Workspace</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Username</label>
            <input 
              type="text" 
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Password</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              required
            />
          </div>

          <div className="flex items-center gap-2 py-2">
            <input 
              type="checkbox" 
              id="remember" 
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="accent-indigo-600"
            />
            <label htmlFor="remember" className="text-sm text-gray-500 cursor-pointer">Remember me on this device</label>
          </div>

          {error && (
            <div className="p-3 bg-rose-50 text-rose-600 rounded-xl text-sm font-medium animate-shake">
              <i className="fa-solid fa-circle-exclamation mr-2"></i>
              {error}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button 
              type="submit" 
              disabled={isLoading}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold shadow-lg shadow-indigo-200 dark:shadow-none transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? <i className="fa-solid fa-spinner animate-spin"></i> : "Sign In"}
            </button>
            
            {onCancel && (
              <button 
                type="button"
                onClick={onCancel}
                className="sm:hidden w-full py-4 bg-gray-100 dark:bg-slate-800 text-gray-500 rounded-2xl font-bold transition-all"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginView;
